import React from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple, MapPin, Buildings, Phone, Globe } from "@phosphor-icons/react";
import { useI18n } from "../../i18n/I18nContext";

const SKOLKOVO_LOGO =
  "https://customer-assets.emergentagent.com/job_inboost-ai-engine/artifacts/5jvrcdjd_Sk_Sk-block-green-ru_word.png";

const REQUISITES = [
  ["ИНН", "7730334680"],
  ["ОГРН", "1257700054336"],
  ["КПП", "771001001"],
  ["ОКВЭД", "62.01, 62.02, 62.03, 62.09, 63.11.1, 63.11.9, 72.19"],
];

export const Footer = () => {
  const { isEn, t } = useI18n();

  return (
    <footer id="contact" data-testid="footer" className="relative border-t border-ink-text/5 pt-20 pb-12 mt-12">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-px bg-gradient-to-r from-transparent via-brand-cyan/60 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2.5 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_inboost-ai-engine/artifacts/lvfcborh_inboost_logo_horizontal_trsp-13.png"
                alt="InBoost"
                className="h-8 w-auto object-contain"
                loading="lazy"
              />
              <div className="h-5 w-px bg-ink-text/10" />
              <div className="mono text-[10px] tracking-[0.22em] text-brand uppercase">AI Engine</div>
            </div>
            <p className="text-2xl sm:text-3xl font-heading font-bold tracking-tight leading-snug max-w-md">
              {t.footer.taglinePart1}{" "}
              <span className="text-brand-cyan">{t.footer.taglineCyan}</span>{" "}
              {t.footer.taglinePart2}{" "}
              <span className="text-muted">{t.footer.taglineMuted}</span>
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {!isEn && (
                <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full glass">
                  <img
                    src={SKOLKOVO_LOGO}
                    alt="Сколково"
                    className="h-4 w-auto object-contain"
                    loading="lazy"
                    data-testid="footer-skolkovo-logo"
                  />
                  <span className="mono text-[10px] uppercase tracking-[0.18em] text-brand-cyan">Резидент</span>
                </div>
              )}
              {isEn && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mono text-[10px] uppercase tracking-[0.18em] text-brand-cyan">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan shadow-[0_0_10px_#0099C9]" />
                  NVIDIA Inception Member
                </div>
              )}
              <Badge>{t.footer.badgePatent}</Badge>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-4">
              {t.footer.contactsTitle}
            </div>
            <div className="space-y-3">
              <a
                href="mailto:inbound@inboost.pro"
                data-testid="footer-email"
                className="group flex items-center gap-2.5 text-ink-text hover:text-brand-cyan transition-colors"
              >
                <EnvelopeSimple size={16} className="text-muted group-hover:text-brand-cyan transition-colors" />
                <span className="mono text-sm">inbound@inboost.pro</span>
              </a>
              {!isEn && (
                <a
                  href="tel:+79103815613"
                  data-testid="footer-phone"
                  className="group flex items-center gap-2.5 text-ink-text hover:text-brand-cyan transition-colors"
                >
                  <Phone size={16} className="text-muted group-hover:text-brand-cyan transition-colors" />
                  <span className="mono text-sm">+7 910 381 56 13</span>
                </a>
              )}
            </div>

            <div className="mt-5 text-xs text-muted leading-relaxed">
              {t.footer.notice}{" "}
              <Link
                to={isEn ? "/en/privacy" : "/privacy"}
                className="text-brand-cyan hover:text-brand-cyan/80 transition-colors"
              >
                {t.footer.privacyLink}
              </Link>
              .{" "}
              <Link
                to={isEn ? "/en/legal" : "/legal"}
                className="text-brand-cyan hover:text-brand-cyan/80 transition-colors"
              >
                {t.footer.legalLink}
              </Link>
              .
            </div>
          </div>
        </div>

        {/* Requisites: only shown in Russian mode */}
        {!isEn ? (
          <div className="glass rounded-2xl p-7 sm:p-8 mb-10" data-testid="footer-requisites">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-4">
                  Юридическое лицо
                </div>
                <div className="flex items-start gap-2.5 mb-4">
                  <Buildings size={16} className="text-muted mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-ink-text font-heading font-semibold text-base">ООО «ИнБуст»</div>
                    <div className="text-muted text-xs mono uppercase tracking-widest mt-1">
                      InBoost AI Engine
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <MapPin size={16} className="text-muted mt-0.5 flex-shrink-0" />
                  <div className="text-muted text-xs leading-relaxed" data-testid="footer-address">
                    127006, г. Москва, вн.тер.г. Муниципальный округ Тверской,
                    ул. Садовая-Триумфальная, д.16, стр.3, помещение 2А/1
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-4">
                  Реквизиты
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {REQUISITES.map(([k, v]) => (
                    <div
                      key={k}
                      className={`${k === "ОКВЭД" ? "sm:col-span-2" : ""}`}
                      data-testid={`requisite-${k.toLowerCase()}`}
                    >
                      <dt className="mono text-[10px] uppercase tracking-widest text-muted-deep mb-1">{k}</dt>
                      <dd className="mono text-sm text-ink-text break-all">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        ) : (
          /* Global English enterprise assurance banner */
          <div className="glass rounded-2xl p-6 sm:p-7 mb-10" data-testid="footer-global-presence">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand/10 border border-brand/30 flex items-center justify-center">
                  <Globe size={20} className="text-brand-cyan" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-base text-ink-text">
                    InBoost AI Engine · Global Enterprise Support
                  </div>
                  <div className="text-muted text-xs mono">
                    High-Performance Kubernetes Infrastructure Orchestration
                  </div>
                </div>
              </div>
              <div className="mono text-xs text-brand-cyan">
                inbound@inboost.pro
              </div>
            </div>
          </div>
        )}

        <div className="pt-8 border-t border-ink-text/5 space-y-4">
          <div className="text-[11px] text-muted-deep leading-relaxed max-w-4xl" data-testid="footer-trademarks">
            {t.footer.trademarks}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="mono text-[11px] text-muted-deep">
              © {new Date().getFullYear()} {t.footer.copyright}
            </div>

            <div className="flex flex-wrap items-center gap-4 mono text-[11px] text-muted-deep">
              <Link to={isEn ? "/en/privacy" : "/privacy"} className="hover:text-brand-cyan transition-colors">
                {t.footer.privacyLink}
              </Link>
              <Link to={isEn ? "/en/legal" : "/legal"} className="hover:text-brand-cyan transition-colors">
                {t.footer.legalLink}
              </Link>
              <span>{t.footer.madeIn}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Badge = ({ children }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mono text-[10px] uppercase tracking-[0.18em] text-brand-cyan">
    <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan shadow-[0_0_10px_#0099C9]" />
    {children}
  </div>
);

export default Footer;